import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Megaphone } from "lucide-react";
import { api } from "../lib/api";
import { cacheGet, cacheSet, cacheKey } from "../lib/cache";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function bannerImageSrc(url) {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return `${BACKEND_URL}${url}`;
}

export default function BannerCarousel({ audience }) {
  const navigate = useNavigate();
  const storeKey = cacheKey("banners", audience);
  const [items, setItems] = useState(() => cacheGet(storeKey).data);
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState({});
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    const cached = cacheGet(storeKey).data;
    if (cached) setItems(cached);
    api
      .get(`/api/banners?audience=${audience}`)
      .then(({ data }) => {
        if (!alive) return;
        setItems(data || []);
        cacheSet(storeKey, data || [], 300000);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [audience, storeKey]);

  useEffect(() => {
    if (!items || items.length < 2) return undefined;
    timer.current = setInterval(() => setIndex((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(timer.current);
  }, [items]);

  useEffect(() => {
    if (items && index >= items.length) setIndex(0);
  }, [items, index]);

  if (!items || items.length === 0) return null;

  const banner = items[index] || items[0];
  const src = bannerImageSrc(banner.image_url);
  const showImage = src && !failed[banner.id];

  const open = () => {
    api.post(`/api/banners/${banner.id}/click`).catch(() => {});
    if (!banner.link_url) return;
    if (/^https?:\/\//.test(banner.link_url)) {
      window.open(banner.link_url, "_blank", "noopener,noreferrer");
    } else {
      navigate(banner.link_url);
    }
  };

  return (
    <div
      className="relative rounded-3xl overflow-hidden border border-brand-100 bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500 text-white animate-fade-up"
      data-testid="banner-carousel"
      role="region"
      aria-label="Banner program"
    >
      <button
        type="button"
        onClick={open}
        className="block w-full text-left"
        data-testid={`banner-slide-${index}`}
        aria-label={banner.title || "Banner"}
      >
        {banner.kind === "IMAGE" && showImage ? (
          <div className="relative w-full aspect-[2/1] max-h-64 min-h-[128px]">
            <img
              src={src}
              alt={banner.title || "Banner"}
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
              onError={() => setFailed((f) => ({ ...f, [banner.id]: true }))}
            />
            {(banner.title || banner.subtitle) && (
              <>
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 z-10 p-4 md:p-5 pr-20">
                  {banner.title && (
                    <p className="text-lg md:text-2xl font-extrabold leading-tight drop-shadow">
                      {banner.title}
                    </p>
                  )}
                  {banner.subtitle && (
                    <p className="text-sm text-white/90 mt-1 line-clamp-2">{banner.subtitle}</p>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            {showImage && (
              <img
                src={src}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                onError={() => setFailed((f) => ({ ...f, [banner.id]: true }))}
              />
            )}
            {showImage && (
              <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/35 to-transparent" />
            )}
            <div className="relative z-10 p-5 md:p-6 min-h-[112px] flex items-center">
              <div className="flex items-start gap-3 md:gap-4 max-w-2xl">
                <span className="hidden sm:grid h-11 w-11 rounded-2xl bg-white/15 place-items-center flex-shrink-0">
                  <Megaphone className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-lg md:text-xl font-extrabold leading-tight">{banner.title}</p>
                  {banner.subtitle && (
                    <p className="text-sm text-white/85 mt-1">{banner.subtitle}</p>
                  )}
                  {banner.link_url && (
                    <span className="inline-flex items-center gap-1 mt-2 text-xs font-bold bg-white/15 rounded-full px-3 py-1">
                      Lihat selengkapnya <ChevronRight className="h-3.5 w-3.5" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </button>

      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + items.length) % items.length)}
            className="absolute z-10 left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/25 hover:bg-black/40 grid place-items-center"
            aria-label="Banner sebelumnya"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % items.length)}
            className="absolute z-10 right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/25 hover:bg-black/40 grid place-items-center"
            aria-label="Banner berikutnya"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute z-10 bottom-3 right-3 flex items-center gap-1.5">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${i === index ? "w-5 bg-white" : "w-1.5 bg-white/50"}`}
                aria-label={`Banner ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
